import { LS } from 'ls-ts';
import { v4 as uuidv4 } from 'uuid';

import { DeviceInfo, TFormData } from '#/client';
import { privacyStoreActions } from '#/plugins/privacy/privacyStore';
import {
  detectBrowserName,
  detectDeviceType,
  detectFullBrowserVersion,
  detectOsName,
  detectOsVersion,
} from '#/service/deviceInfo';

import { StorageKeys } from '../constants';

export const getDeviceId = (): string => {
  const deviceIdFromConfig = LS.read<string>(StorageKeys.DEVICE_ID, '');

  if (deviceIdFromConfig) {
    return deviceIdFromConfig;
  }

  // if storage returns empty, generate a unique one and save it
  const deviceId = uuidv4();

  LS.write(StorageKeys.DEVICE_ID, deviceId);

  return deviceId;
};

/**
 * Compiles the device info object that we will send with every Core API request as a header.
 * @see https://docs.one.accedo.io/one-core/delivery-api/identity/device-network-info/#supported-fields-and-values
 * - Device info: Note that there are common field/value definitions and web-specific ones listed separately.
 * - Network info: Omitted, because we don't know (as the docs say).
 * - Legal consent info: Specs are on the same page in another section.
 * @param overwriteDeviceId (GUID) Use a different deviceId than the one stored in LocalStorage. Normally you should leave it empty (to use value from LS), as it's used only in very specific scenarios
 * @returns {DeviceInfo} The device info object.
 */
export const getDeviceInfo = (overwriteDeviceId?: string): DeviceInfo => {
  const NA = 'N/A';
  const deviceType = detectDeviceType();
  const consents = privacyStoreActions.getConsents();

  return {
    // Common. Device ID.
    id: overwriteDeviceId ?? getDeviceId(),
    hardware: {
      // Specific. Browser and device: "Browser (Phone)", "Browser (Tablet)", ... or "N/A"
      manufacturer: deviceType ? `Browser (${deviceType})` : NA,
      // Specific. Browser name: "Chrome", "Safari", ... or "N/A"
      model: detectBrowserName() || NA,
      // Specific. Full browser version: "3.1.2", "68.2541.00214", ... or "N/A"
      version: detectFullBrowserVersion() || NA,
    },
    os: {
      // Specific. OS name: "macOS", "Windows", ... or "N/A"
      name: detectOsName() || NA,
      // Specific. OS version: "11.2", "10", ... or "N/A"
      version: detectOsVersion() || NA,
    },
    display: {
      // Common. Display width in pixels: 1920
      width: window.screen.width,
      // Common. Display height in pixels: 1080
      height: window.screen.height,
    },
    // Legal consent info
    legal: {
      tcfv2: consents?.gdpr?.tcfv2,
      customVendors: consents?.gdpr?.customVendors,
    },
  };
};

export function populateDataWithDeviceInfo(data: TFormData) {
  const deviceInfo = getDeviceInfo();

  return {
    values: { ...data },
    deviceInfo,
  };
}
